import { useCan } from '@features/auth';
import {
    Badge,
    Card,
    CardHeader,
    EmptyState,
    ErrorState,
    LoadingRows,
    PropagationNotice,
    Toggle,
    WriteError,
} from '@shared/ui';
import { useState } from 'react';
import { Link } from 'react-router';

import { useClearTenantOverride, useSetTenantOverride, useTenantFlags } from '../hooks/use-flags';
import { FLAG_TYPE_LABEL, FLAG_TYPE_TONE } from '../schemas/flag-type';

import { type OverrideChange } from './override-confirm-copy';
import { ReadOnlyNotice } from './ReadOnlyNotice';
import { offersRollout } from './rollout-ui';
import { tenantFlagRow, type TenantFlagRow } from './tenant-flag-row';
import { TenantOverrideConfirmDialog } from './TenantOverrideConfirmDialog';
import { VerdictBadge } from './VerdictBadge';

import type { TenantFlagView } from '../schemas/tenant-flag';

/**
 * Every flag, and what this one museum gets — the inverse of `FlagDetailPage`.
 *
 * ## Why it lists the whole catalog rather than just the overrides
 *
 * The question this screen answers is "what does this client have", and a list
 * of override rows cannot answer it: a museum with no rows at all still
 * receives whatever the platform defaults say. Showing only overrides would
 * render an empty table for a client that in fact has a dozen features on, and
 * leave the operator with nothing to turn *on* — the row they would need does
 * not exist yet, which is precisely why the flag-side dialog made them type a
 * tenant id by hand.
 *
 * ## One switch here, two columns there
 *
 * `FlagDetailPage` deliberately splits the stored value from the resolved
 * verdict, because it lists museums that have already committed to a stored
 * value. Here most rows have no stored value at all, so the switch shows what
 * the museum *gets* and the badge beside it says where that came from. Turning
 * it is then a single, honest statement — "this museum gets this" — and the two
 * rows where the platform overrules the switch entirely (archived, rollout) say
 * so instead of offering a control that would lie.
 */
export function TenantFlagsPanel({ tenantId }: { tenantId: string }) {
    const flags = useTenantFlags(tenantId);

    if (flags.isPending) return <LoadingRows label="Loading this client's flags…" />;

    if (flags.isError) {
        return (
            <ErrorState
                error={flags.error}
                onRetry={() => {
                    void flags.refetch();
                }}
            />
        );
    }

    const overridden = flags.data.filter((view) => view.override !== null).length;

    return (
        <>
            <ReadOnlyNotice />
            <PropagationNotice />

            <Card>
                <CardHeader
                    title="Feature flags for this client"
                    subtitle={
                        overridden === 0
                            ? 'Every flag resolves the platform default'
                            : `${String(overridden)} of ${String(flags.data.length)} set for this client specifically`
                    }
                />

                {flags.data.length === 0 ? (
                    <EmptyState
                        title="There are no flags yet"
                        description="Nothing has been added to the catalog, so there is nothing to turn on for this client."
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-surface-sunken text-left text-[11px] uppercase tracking-wide text-content-muted">
                                    <th className="px-5 py-3 font-semibold">Flag</th>
                                    {/*
                                     * The type is a column here, not a tab as
                                     * on the flags list. That list is browsed
                                     * by engineers working on one side at a
                                     * time; this one answers "what does this
                                     * client have", and splitting it would
                                     * make that question need two clicks and a
                                     * mental join. What it must not do is
                                     * leave the axis invisible — the two reach
                                     * different systems, and one of them is
                                     * the customer's own Salesforce org.
                                     */}
                                    <th className="px-5 py-3 font-semibold">Type</th>
                                    <th className="px-5 py-3 font-semibold">This client</th>
                                    <th className="px-5 py-3 font-semibold">Resolves to</th>
                                    <th className="px-5 py-3 font-semibold">Set by</th>
                                </tr>
                            </thead>
                            <tbody>
                                {flags.data.map((view) => (
                                    <FlagRow
                                        key={view.definition.key}
                                        tenantId={tenantId}
                                        view={view}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </>
    );
}

function FlagRow({ tenantId, view }: { tenantId: string; view: TenantFlagView }) {
    const row = tenantFlagRow(view);
    const [confirming, setConfirming] = useState<OverrideChange | null>(null);
    const setOverride = useSetTenantOverride(tenantId);
    const clearOverride = useClearTenantOverride(tenantId);
    const busy = setOverride.isPending || clearOverride.isPending;
    const error = setOverride.error ?? clearOverride.error;
    // One tier for both directions. Turning a flag *off* for a museum that
    // bought the feature is as consequential as turning one on, and reverting
    // to a default that is `on` enables something for a customer — so neither
    // is the gentler of the two.
    const canOverride = useCan('ops-csm');

    /**
     * The write the switch would issue, built once so the confirm step and the
     * unconfirmed path cannot drift into sending different things.
     */
    const pendingWrite: OverrideChange | null =
        row.toggle.action === 'set'
            ? {
                  tenantId,
                  enabled: row.toggle.enabled,
                  // Round-trip this museum's own gate. Sending only `enabled`
                  // would silently promote a 25% override to 100% for it, and
                  // nothing downstream could tell that apart from intent.
                  rolloutPercentage: view.override?.rolloutPercentage,
              }
            : null;

    const write = () => {
        if (!pendingWrite) return;
        setOverride.mutate(
            {
                key: row.key,
                enabled: pendingWrite.enabled,
                ...(pendingWrite.rolloutPercentage !== undefined
                    ? { rolloutPercentage: pendingWrite.rolloutPercentage }
                    : {}),
            },
            {
                onSuccess: () => {
                    setConfirming(null);
                },
            },
        );
    };

    const change = () => {
        switch (row.toggle.action) {
            case 'set':
                // Opens the dialog *instead of* writing on the one case the
                // flag screen also stops for — this museum's first override.
                if (row.confirms) setConfirming(pendingWrite);
                else write();
                return;
            case 'clear':
                clearOverride.mutate({ key: row.key });
                return;
            case 'none':
                return;
        }
    };

    return (
        <>
            <tr className="border-t border-line-subtle text-[13.5px] hover:bg-surface-sunken">
                <td className="px-5 py-3">
                    <Link
                        to={`/flags/${encodeURIComponent(row.key)}`}
                        className="font-medium text-content-strong hover:text-accent-icon"
                    >
                        {row.name}
                    </Link>
                    <div className="font-mono text-xs text-content-faint">{row.key}</div>
                </td>
                <td className="px-5 py-3">
                    <Badge tone={FLAG_TYPE_TONE[view.definition.type]}>
                        {FLAG_TYPE_LABEL[view.definition.type]}
                    </Badge>
                </td>
                <td className="px-5 py-3">
                    {row.toggle.action === 'none' ? (
                        <span className="text-xs text-content-faint">
                            Archived — never served, override or not
                        </span>
                    ) : (
                        <>
                            <Toggle
                                on={row.enabled}
                                busy={busy}
                                showLabel
                                onChange={canOverride ? change : undefined}
                                aria-label={`${row.key} for this client`}
                            />
                            {/*
                             * A tenant view has no visitor to bucket, so the
                             * server reports the gate's on-value. Without this
                             * line the switch reads as "on for this museum"
                             * when it means "on for some of its visitors".
                             *
                             * Behind `offersRollout` with every other rollout
                             * note: while `ROLLOUT_UI_ENABLED` is false the UI
                             * says nothing about gates anywhere, and a caveat
                             * surfacing only here would be the one place a
                             * concept the rest of the app hides leaks out.
                             */}
                            {row.gated && offersRollout(view.definition.type) && (
                                <div className="mt-1 text-xs text-content-faint">
                                    Gated — only some visitors
                                </div>
                            )}
                        </>
                    )}
                </td>
                <td className="px-5 py-3">
                    <VerdictBadge verdict={view.verdict} />
                </td>
                <td className="px-5 py-3">
                    {row.origin === 'override' ? (
                        <Badge tone="info">This client</Badge>
                    ) : (
                        <span className="text-xs text-content-faint">Platform default</span>
                    )}

                    {/*
                     * Inside a cell, not in a row of its own. `Modal` is a
                     * native `<dialog>` rendered in place — `showModal()` lifts
                     * it into the top layer, so the table's layout does not
                     * reach it, but a `hidden` ancestor would stop it rendering
                     * at all. A `<dialog>` is flow content and belongs in a
                     * `<td>`; directly under `<tbody>` it would not.
                     */}
                    <TenantOverrideConfirmDialog
                        flagKey={row.key}
                        kind={view.definition}
                        pending={confirming}
                        isPending={setOverride.isPending}
                        error={setOverride.error}
                        onCancel={() => {
                            setOverride.reset();
                            setConfirming(null);
                        }}
                        onConfirm={write}
                    />
                </td>
            </tr>
            {/*
             * Only when the dialog is closed. While it is open the error
             * belongs inside it, next to the button that caused it — repeating
             * it in the row behind would read as two failures.
             */}
            {error && confirming === null && (
                <tr className="border-t border-line-subtle">
                    <td colSpan={5} className="px-5 py-2.5">
                        <WriteError error={error} />
                    </td>
                </tr>
            )}
        </>
    );
}

export type { TenantFlagRow };
